import React, { useState } from 'react';
import { 
  FileCode, 
  UploadCloud, 
  CheckCircle2, 
  AlertCircle, 
  X, 
  RefreshCw, 
  Sparkles, 
  Layers, 
  Copy, 
  Check, 
  Info,
  ShieldCheck
} from 'lucide-react';
import { apiPost } from '../lib/api';
import { useLanguage } from '../lib/i18n';

interface BulkExerciseImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (summary: { inserted: number; updated: number; skipped: number; total: number }) => void;
  defaultModuleId?: string;
}

const SAMPLE_JSON = `[
  {
    "id": "rehab_cat_cow_stretch",
    "name": "کشش گربه-شتر برای کاهش فشار دیسک کمر",
    "category": "rehab",
    "description": "چهار دست و پا قرار بگیرید. با بازدم ستون فقرات را به بالا گرد کنید و با دم به آرامی به حالت اول بازگردید.",
    "purpose": "کاهش اسپاسم عضلات ستون فقرات، آزادسازی ریشه‌های عصبی و افزایش انعطاف‌پذیری مهره‌های کمری",
    "target_muscles": "عضلات راست‌کننده ستون فقرات، فیله کمری و عضلات عمقی شکم",
    "sets": 3,
    "reps": "12 تکرار",
    "rest_time": "30 ثانیه",
    "duration_minutes": 4,
    "difficulty": "Beginner"
  },
  {
    "id": "rehab_glute_bridge_isometric",
    "name": "پل باسن ایزومتریک با انقباض متمرکز",
    "category": "rehab",
    "description": "به پشت بخوابید، زانوها خم و کف پاها روی زمین. لگن را بلند کرده و در بالاترین نقطه ۳ ثانیه مکث کنید.",
    "purpose": "تقویت عضلات سرینی و ثبات مفصل خاجی-خاصره‌ای (SI Joint) برای کاهش بار وارده بر دیسک L4-L5",
    "target_muscles": "سرینی بزرگ، عضلات همسترینگ و فیله کمر",
    "sets": 3,
    "reps": "10 تکرار با مکث",
    "rest_time": "45 ثانیه",
    "duration_minutes": 5,
    "difficulty": "Beginner"
  },
  {
    "id": "rehab_child_pose_decompression",
    "name": "حرکت کودک با کشش طولی ستون مهره‌ها",
    "category": "rehab",
    "description": "روی زانوها نشسته و دست‌ها را به سمت جلو روی زمین بکشید. تنفس عمیق شکمی داشته باشید.",
    "purpose": "دکامپرشن و ایجاد فاصله ملایم بین مهره‌های لومبار و تسکین دردهای ارجاعی سیاتیک",
    "target_muscles": "عضلات پشتی بزرگ (لاتیسیموس) و اکستنسورهای کمری",
    "sets": 2,
    "reps": "60 ثانیه مکث",
    "rest_time": "30 ثانیه",
    "duration_minutes": 3,
    "difficulty": "Beginner"
  }
]`;

export const BulkExerciseImportModal: React.FC<BulkExerciseImportModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultModuleId = 'module_1_rehab'
}) => {
  const { lang, t } = useLanguage();
  const [jsonText, setJsonText] = useState(SAMPLE_JSON);
  const [moduleId, setModuleId] = useState(defaultModuleId);
  const [duplicateStrategy, setDuplicateStrategy] = useState<'update' | 'skip' | 'insert'>('update');
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [parsedItems, setParsedItems] = useState<any[] | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [copiedSample, setCopiedSample] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    inserted: number;
    updated: number;
    skipped: number;
    total: number;
    errors?: string[];
  } | null>(null);

  if (!isOpen) return null;

  const handleValidate = () => {
    setValidationError(null);
    setParsedItems(null);
    setImportResult(null);

    const trimmed = jsonText.trim();
    if (!trimmed) {
      setValidationError(t('jsonEmptyError', 'لطفاً متن JSON فعالیت‌ها را در کادر وارد نمایید.'));
      return;
    }

    try {
      const parsed = JSON.parse(trimmed);
      let items: any[] = [];

      if (Array.isArray(parsed)) {
        items = parsed;
      } else if (parsed && typeof parsed === 'object') {
        if (Array.isArray(parsed.activities)) {
          items = parsed.activities;
        } else if (Array.isArray(parsed.exercises)) {
          items = parsed.exercises;
        } else if (Array.isArray(parsed.items)) {
          items = parsed.items;
        } else {
          items = [parsed];
        }
      }

      if (items.length === 0) {
        setValidationError(t('jsonNoItemsFound', 'هیچ شیء یا آرایه‌ای از فعالیت‌ها در داده‌های ورودی یافت نشد.'));
        return;
      }

      // Check item validity
      const validItems = items.filter(it => it && typeof it === 'object' && (it.name || it.title || it.activity_name));
      if (validItems.length === 0) {
        setValidationError(t('jsonMissingNameError', 'تمام تمرینات باید حداقل شامل فیلد "name" یا "title" باشند.'));
        return;
      }

      setParsedItems(items);
    } catch (err: any) {
      setValidationError(`${t('jsonSyntaxError', 'خطای ساختار JSON:')} ${err.message}`);
    }
  };

  const handleExecuteImport = async () => {
    if (!parsedItems || parsedItems.length === 0) {
      handleValidate();
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const payload = {
        moduleId: moduleId || 'module_1_rehab',
        duplicateStrategy,
        activities: parsedItems
      };

      const res = await apiPost<any>('/api/rehab/exercises/bulk', payload);

      if (res.success && res.data) {
        const summary = res.data.summary || {
          inserted: res.data.inserted ?? parsedItems.length,
          updated: res.data.updated ?? 0,
          skipped: res.data.skipped ?? 0,
          total: parsedItems.length
        };

        setImportResult({
          success: true,
          inserted: summary.inserted,
          updated: summary.updated,
          skipped: summary.skipped,
          total: summary.total,
          errors: res.data.errors
        });

        onSuccess(summary);
      } else {
        setImportResult({
          success: false,
          inserted: 0,
          updated: 0,
          skipped: 0,
          total: parsedItems.length,
          errors: [res.error || t('importFailed', 'درون‌ریزی به پایگاه داده با خطا مواجه شد.')]
        });
      }
    } catch (err: any) {
      setImportResult({
        success: false,
        inserted: 0,
        updated: 0,
        skipped: 0,
        total: parsedItems.length,
        errors: [err.message || t('networkError', 'خطا در برقراری ارتباط با سرور')]
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleLoadSample = () => {
    setJsonText(SAMPLE_JSON);
    setParsedItems(null);
    setValidationError(null);
    setImportResult(null);
  };

  const handleCopySample = () => {
    navigator.clipboard?.writeText(SAMPLE_JSON);
    setCopiedSample(true);
    setTimeout(() => setCopiedSample(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-fadeIn overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 w-full max-w-3xl rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="p-4 sm:p-5 bg-gradient-to-r from-emerald-600 to-teal-700 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-xl backdrop-blur-md">
              <UploadCloud className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold font-display">
                {t('bulkImportModalTitle', 'درون‌ریزی دسته‌جمعی تمرینات و فعالیت‌ها (JSON Bulk Import)')}
              </h2>
              <p className="text-xs text-emerald-100 opacity-90">
                {t('bulkImportModalSubtitle', 'ثبت و ذخیره پایدار در پایگاه داده ابری Cloudflare D1')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 flex-1 text-xs">
          
          {/* Info Banner */}
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/50 rounded-xl text-emerald-900 dark:text-emerald-200 flex items-start gap-2.5">
            <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-semibold text-xs">
                {t('bulkImportD1NoticeTitle', 'ذخیره‌سازی دائمی و یکپارچه در پایگاه داده Cloudflare D1')}
              </p>
              <p className="text-[11px] leading-relaxed text-emerald-800 dark:text-emerald-300">
                {t('bulkImportD1NoticeDesc', 'تمام فعالیت‌های وارد شده مستقیماً در جدول دیتابیس با شناسه اختصاصی و رابطه ماژول ثبت می‌شوند و با تغییر صفحه، رفرش یا خروج از حساب کاربری کاملاً پایدار خواهند ماند.')}
              </p>
            </div>
          </div>

          {/* Module & Strategy Config */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-gray-700 dark:text-gray-300 mb-1">
                {t('bulkImportModuleSelect', 'ماژول مقصد (Destination Module):')}
              </label>
              <select
                value={moduleId}
                onChange={(e) => setModuleId(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-medium text-xs"
              >
                <option value="module_1_rehab">{t('module1RehabName', 'فیزیوتراپی و تمرینات اصلاحی')}</option>
                <option value="module_2_belly">{t('module2BellyName', 'تمرینات چربی‌سوزی و کاردیو')}</option>
                <option value="custom_activities">{t('moduleCustomActivities', 'فعالیت‌های عمومی روزانه')}</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-gray-700 dark:text-gray-300 mb-1">
                {t('bulkImportStrategySelect', 'راهبرد مدیریت موارد تکراری (Duplicate Strategy):')}
              </label>
              <select
                value={duplicateStrategy}
                onChange={(e) => setDuplicateStrategy(e.target.value as any)}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-medium text-xs"
              >
                <option value="update">{t('strategyUpdate', 'به‌روزرسانی موارد موجود + درج موارد جدید (Update / Upsert)')}</option>
                <option value="skip">{t('strategySkip', 'صرف‌نظر از تکراری‌ها + فقط درج موارد جدید (Skip duplicates)')}</option>
                <option value="insert">{t('strategyInsert', 'همیشه درج با شناسه جدید (Always Insert as New)')}</option>
              </select>
            </div>
          </div>

          {/* JSON Input Area */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="block text-[11px] font-bold text-gray-700 dark:text-gray-300">
                {t('bulkImportJsonLabel', 'محتوای JSON آرایه تمرینات:')}
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopySample}
                  className="text-[11px] text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 flex items-center gap-1 cursor-pointer transition-colors"
                >
                  {copiedSample ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedSample ? t('copied', 'کپی شد') : t('copySample', 'کپی نمونه')}</span>
                </button>
                <button
                  type="button"
                  onClick={handleLoadSample}
                  className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>{t('loadSampleJson', 'بارگذاری نمونه JSON')}</span>
                </button>
              </div>
            </div>

            <textarea
              value={jsonText}
              onChange={(e) => {
                setJsonText(e.target.value);
                setParsedItems(null);
                setValidationError(null);
                setImportResult(null);
              }}
              rows={8}
              dir="ltr"
              placeholder='[ { "name": "...", "sets": 3, "reps": "10", ... } ]'
              className="w-full font-mono text-[11px] p-3 bg-gray-900 text-emerald-300 rounded-xl border border-gray-700 focus:border-emerald-500 focus:outline-hidden leading-relaxed shadow-inner"
            />
          </div>

          {/* Validation Feedback */}
          {validationError && (
            <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-xl text-rose-800 dark:text-rose-200 flex items-start gap-2 animate-fadeIn">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <span>{validationError}</span>
            </div>
          )}

          {/* Parsed Items Preview */}
          {parsedItems && parsedItems.length > 0 && (
            <div className="space-y-2 border border-gray-200 dark:border-gray-800 rounded-xl p-3 bg-gray-50/50 dark:bg-gray-800/40 animate-fadeIn">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 font-bold text-gray-800 dark:text-gray-200">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>
                    {t('validItemsCount', 'تعداد {{count}} تمرین معتبر آماده درون‌ریزی یافت شد:', { count: parsedItems.length })}
                  </span>
                </div>
                <span className="text-[10px] px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200 rounded-md font-bold">
                  {t('readyToImport', 'آماده ارسال به D1')}
                </span>
              </div>

              <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
                {parsedItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs"
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <span className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 flex items-center justify-center text-[10px] font-bold shrink-0">
                        {idx + 1}
                      </span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200 truncate">
                        {item.name || item.title || item.activity_name || `تمرین #${idx + 1}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 shrink-0">
                      <span className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                        {item.sets || 3} ست
                      </span>
                      <span className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                        {item.reps || '10 تکرار'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Import Result Toast */}
          {importResult && (
            <div
              className={`p-3.5 rounded-xl border flex items-start gap-2.5 animate-fadeIn ${
                importResult.success
                  ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200'
                  : 'bg-rose-50 dark:bg-rose-950/40 border-rose-300 dark:border-rose-800 text-rose-900 dark:text-rose-200'
              }`}
            >
              {importResult.success ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              )}
              <div className="space-y-1">
                <p className="font-bold text-xs">
                  {importResult.success
                    ? t('importSuccessTitle', 'عملیات درون‌ریزی در دیتابیس با موفقیت انجام شد!')
                    : t('importFailedTitle', 'خطا در فرآیند درون‌ریزی')}
                </p>
                {importResult.success && (
                  <p className="text-[11px]">
                    {t('importSummaryDesc', 'مجموع: {{total}} | افزوده شده: {{ins}} | به‌روزرسانی شده: {{upd}} | رد شده: {{skp}}', {
                      total: importResult.total,
                      ins: importResult.inserted,
                      upd: importResult.updated,
                      skp: importResult.skipped
                    })}
                  </p>
                )}
                {importResult.errors && importResult.errors.length > 0 && (
                  <ul className="list-disc list-inside text-[11px] text-rose-700 dark:text-rose-300">
                    {importResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Footer Actions */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/80 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-xl text-xs font-semibold cursor-pointer transition-colors"
          >
            {t('close', 'بستن')}
          </button>

          <div className="flex items-center gap-2">
            {!parsedItems ? (
              <button
                type="button"
                onClick={handleValidate}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 cursor-pointer"
              >
                <FileCode className="w-4 h-4" />
                <span>{t('validateJsonBtn', 'اعتبارسنجی داده‌ها (Validate JSON)')}</span>
              </button>
            ) : (
              <button
                type="button"
                disabled={isImporting}
                onClick={handleExecuteImport}
                className={`px-5 py-2 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-2 cursor-pointer ${
                  isImporting
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-500/20'
                }`}
              >
                {isImporting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{t('importingProgress', 'در حال ذخیره‌سازی در D1...')}</span>
                  </>
                ) : (
                  <>
                    <UploadCloud className="w-4 h-4" />
                    <span>
                      {t('startImportBtn', 'انجام درون‌ریزی به پایگاه داده ({{count}} فعالیت)', { count: parsedItems.length })}
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default BulkExerciseImportModal;
